import { useMemo } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import TableContainer from "components/common/TableContainer";
import Avatar from "components/common/Avatar";
import { getAttendanceList } from "features/EmployeeAttendance/services/EmployeeAttendanceService";
import type { AttendanceListRow } from "features/EmployeeAttendance/types/employee-attendance.types";
import moment from "moment";

function resolveName(row: AttendanceListRow) {
  const first = row.user?.firstName ?? "";
  const last = row.user?.lastName ?? "";
  return [first, last].filter(Boolean).join(" ").trim() || row.code || "Unknown";
}

export function OnLeaveTodayCard() {
  const today = moment().format("YYYY-MM-DD");

  const requestParams = useMemo(
    () => ({ date: today, status: "LEAVE" }),
    [today],
  );

  const columns: ColumnDef<AttendanceListRow>[] = useMemo(
    () => [
      {
        header: "Employee",
        id: "employee",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const name = resolveName(row.original);
          const avatar = row.original.user?.avatar ?? undefined;
          const code = row.original.code;
          return (
            <div className="d-flex align-items-center gap-2">
              <div className="flex-shrink-0 avatar-xs">
                <Avatar
                  name={name}
                  image={avatar}
                  className="rounded-circle w-100 h-100"
                />
              </div>
              <div className="min-w-0">
                <div className="fw-medium text-truncate small">{name}</div>
                {code ? (
                  <div className="text-muted" style={{ fontSize: "11px" }}>
                    {code}
                  </div>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        header: "Department",
        id: "department",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="small text-muted">
            {row.original.department?.name ?? "—"}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <Card className="mb-4">
      <CardHeader className="d-flex align-items-center justify-content-between">
        <div>
          <h5 className="card-title mb-0">On leave today</h5>
          <p className="text-muted mb-0" style={{ fontSize: "0.75rem" }}>
            Employees on leave today
          </p>
        </div>
        <i className="ri-rest-time-line fs-20 text-muted" />
      </CardHeader>
      <CardBody>
        <TableContainer
          columns={columns}
          fetchApi={getAttendanceList}
          requestParams={requestParams}
          syncPaginationToUrl={false}
          pageSize={10}
          globalSearch={false}
          hidePageSize
          divClass="table-responsive table-card mb-0"
          tableClass="table-nowrap align-middle mb-0"
          theadClass="table-light text-muted"
        />
      </CardBody>
    </Card>
  );
}
